/** SUNTRONIX ONE — Supplier Workspace.
 *
 *  Route: /supplier
 *  Rendered ONLY for role in {supplier_user, supplier_admin}.  Every
 *  API call scopes strictly to the caller's vendor_id.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Package, ShieldCheck, RefreshCw, ChevronLeft, FileText, Truck,
  ReceiptText, CheckCircle2, XCircle, Upload, Plus, Clock3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fmtCcy = (n, ccy) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency",
    currency: ccy || "INR", maximumFractionDigits: 2 }).format(n);


export default function SupplierWorkspace() {
  const { user, loading } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [poId, setPoId] = useState(sp.get("po") || null);
  const [meta, setMeta] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [pos, setPos] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user || !["supplier_user", "supplier_admin"].includes(user.role)) return;
    setBusy(true);
    try {
      const [m, d, pl] = await Promise.all([
        api.get("/supplier/meta"),
        api.get("/supplier/dashboard"),
        api.get("/supplier/pos"),
      ]);
      setMeta(m.data); setDashboard(d.data); setPos(pl.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-[12px] text-slate-500">Loading…</div>;
  if (!user) { window.location.href = "/login"; return null; }
  if (!["supplier_user", "supplier_admin"].includes(user.role)) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center p-8">
        <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-md text-center">
          <div className="text-[13px] font-heading font-semibold text-slate-900 mb-1">
            Supplier Workspace
          </div>
          <div className="text-[12px] text-slate-600">
            You are signed in as <b>{user.email}</b> ({user.role}) — this
            workspace is only accessible to authorised supplier users.
          </div>
        </div>
      </div>
    );
  }

  const setPo = (id) => {
    setPoId(id);
    const s = new URLSearchParams(sp);
    if (id) s.set("po", id); else s.delete("po");
    setSp(s, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1500px] mx-auto p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-indigo-800 text-white grid place-items-center">
            <Package className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-[14px] font-heading font-semibold text-slate-900">
              Supplier Workspace
            </div>
            <div className="text-[10px] text-slate-500">
              {meta?.current_user?.vendor_name || "Supplier"} · {user?.email}
            </div>
          </div>
          <button onClick={load} disabled={busy}
              data-testid="sw-refresh"
              className="text-[12px] px-2.5 py-1 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1">
            <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto p-4 space-y-4" data-testid="supplier-workspace">
        {poId ? (
          <PoDetail poId={poId} onBack={() => setPo(null)} onChanged={load} />
        ) : (
          <>
            <Dashboard dashboard={dashboard} />
            <PoList pos={pos} onOpen={setPo} />
          </>
        )}
      </main>
    </div>
  );
}


function Dashboard({ dashboard }) {
  const t = dashboard?.tiles || {};
  const tiles = [
    ["Open POs", t.open_pos, "bg-slate-100"],
    ["Action required", t.action_required, "bg-amber-100 text-amber-900"],
    ["Accepted", t.accepted, "bg-emerald-100 text-emerald-900"],
    ["Upcoming delivery", t.upcoming_delivery, "bg-sky-100 text-sky-900"],
    ["Partial delivery", t.partial_delivery, "bg-fuchsia-100 text-fuchsia-900"],
    ["Invoice review", t.invoice_review, "bg-indigo-100 text-indigo-900"],
    ["Completed", t.completed, "bg-slate-200 text-slate-800"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="sw-dashboard">
      {tiles.map(([label, value, tone]) => (
        <div key={label} className={"rounded-lg p-3 " + tone}>
          <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
          <div className="text-[22px] font-heading font-semibold mt-1">{value ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}


function PoList({ pos, onOpen }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="sw-po-list">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">PO Number</th>
            <th className="p-2 text-left">PO Date</th>
            <th className="p-2 text-left">Lines</th>
            <th className="p-2 text-left">Status</th>
            <th className="p-2 text-left">Your response</th>
            <th className="p-2 text-right">Total</th>
            <th className="p-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pos.map((po) => (
            <tr key={po.id} className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`sw-po-${po.id}`}>
              <td className="p-2 font-semibold">{po.po_number || po.code}</td>
              <td className="p-2 font-mono text-[10px]">{po.po_date?.slice(0, 10)}</td>
              <td className="p-2">{po.items_count}</td>
              <td className="p-2">{po.status}</td>
              <td className="p-2">
                {po.response_kind
                  ? <span className={"text-[10px] px-1.5 py-0.5 rounded ring-1 " +
                        (po.response_kind === "ACCEPT" ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                                                                                  : "bg-amber-100 text-amber-800 ring-amber-200")}>
                        {po.response_kind}
                      </span>
                  : <span className="text-slate-400">—</span>}
              </td>
              <td className="p-2 text-right font-mono">{fmtCcy(po.total, po.currency)}</td>
              <td className="p-2 text-right">
                <button onClick={() => onOpen(po.id)}
                    data-testid={`sw-open-${po.id}`}
                    className="text-[10px] px-2 py-1 rounded bg-indigo-800 text-white">
                  View
                </button>
              </td>
            </tr>
          ))}
          {!pos.length && (
            <tr><td colSpan={7} className="p-8 text-center text-slate-400">
              No POs yet.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function PoDetail({ poId, onBack, onChanged }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const load = useCallback(async () => {
    try { setD((await api.get(`/supplier/pos/${poId}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [poId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-[12px] text-slate-500">Loading…</div>;
  const po = d.po;

  return (
    <div className="space-y-3" data-testid="sw-po-detail">
      <div className="flex items-center gap-2">
        <button onClick={onBack} data-testid="sw-back"
            className="text-[12px] px-2.5 py-1 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="text-[13px] font-heading font-semibold">
          {po.po_number || po.code} <span className="text-slate-400 font-mono text-[10px]">· {po.status}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <MiniStat k="PO Date" v={po.po_date?.slice(0, 10)} />
        <MiniStat k="Required by" v={po.required_delivery_date?.slice(0, 10)} />
        <MiniStat k="Delivery" v={po.delivery_location} />
        <MiniStat k="Total" v={fmtCcy(po.total, po.currency)} />
      </div>

      <div className="border-b border-slate-200 flex flex-wrap gap-1">
        {[["overview", "Overview"], ["actions", "Actions"], ["dispatch", "Dispatch"],
          ["invoice", "Invoice"], ["documents", "Documents"], ["audit", "Audit"]].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)}
              data-testid={`sw-tab-${k}`}
              className={"px-3 py-1.5 text-[12px] -mb-px border-b-2 " +
                              (tab === k ? "border-indigo-800 text-indigo-900 font-medium"
                                                        : "border-transparent text-slate-500 hover:text-slate-900")}>
            {v}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview po={po} d={d} />}
      {tab === "actions" && <Actions poId={poId} po={po} onDone={() => { load(); onChanged?.(); }} />}
      {tab === "dispatch" && <DispatchTab poId={poId} d={d} onDone={load} />}
      {tab === "invoice" && <InvoiceTab poId={poId} d={d} onDone={load} />}
      {tab === "documents" && <DocumentsTab poId={poId} d={d} onDone={load} />}
      {tab === "audit" && <AuditTab d={d} />}
    </div>
  );
}


function Overview({ po, d }) {
  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 p-2">Line items</div>
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Part</th>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-right">Qty</th>
              <th className="p-2 text-right">Unit Price</th>
              <th className="p-2 text-right">Line Total</th>
              <th className="p-2 text-right">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {(po.items || []).map((it) => {
              const outstanding = (d.outstanding_qty || []).find(o => o.line_id === (it.id || it.line_id));
              return (
                <tr key={it.id || it.line_id} className="border-t border-slate-100">
                  <td className="p-2 font-mono">{it.part_number || "—"}</td>
                  <td className="p-2">{it.description || it.product_name}</td>
                  <td className="p-2 text-right font-mono">{it.quantity}</td>
                  <td className="p-2 text-right font-mono">{fmtCcy(it.unit_price, po.currency)}</td>
                  <td className="p-2 text-right font-mono">{fmtCcy(it.line_total, po.currency)}</td>
                  <td className="p-2 text-right font-mono text-amber-800">
                    {outstanding ? outstanding.outstanding : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {d.etas.length > 0 && (
        <div className="border border-slate-200 rounded-lg bg-white p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Latest ETA confirmation</div>
          <div className="text-[11px] mt-1">
            Dispatch: <b>{d.etas[0].expected_dispatch_date || "—"}</b> · Delivery: <b>{d.etas[0].expected_delivery_date || "—"}</b>
          </div>
        </div>
      )}
    </div>
  );
}


function Actions({ poId, po, onDone }) {
  const [kind, setKind] = useState("ACCEPT");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [expDispatch, setExpDispatch] = useState("");
  const [expDeliver, setExpDeliver] = useState("");
  const [avail, setAvail] = useState("");
  const [busy, setBusy] = useState(false);

  const respond = async () => {
    setBusy(true);
    try {
      await api.post(`/supplier/pos/${poId}/response`,
        { kind, reason, notes });
      toast.success(`Response recorded: ${kind}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitEta = async () => {
    setBusy(true);
    try {
      await api.post(`/supplier/pos/${poId}/eta`,
        { expected_dispatch_date: expDispatch || null,
          expected_delivery_date: expDeliver || null,
          available_qty: avail ? parseFloat(avail) : null,
          notes });
      toast.success("ETA confirmed");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid="sw-actions-response">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">PO Response</div>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
            data-testid="sw-response-kind"
            className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
          {["ACCEPT", "PARTIAL", "REQUEST_CHANGE", "DECLINE"].map((k) =>
            <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)}
            rows={2} placeholder="Reason (if change/decline/partial)"
            data-testid="sw-response-reason"
            className="mt-2 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2} placeholder="Notes"
            className="mt-2 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <button onClick={respond} disabled={busy}
            data-testid="sw-response-submit"
            className="mt-2 w-full text-[12px] py-2 rounded-md bg-emerald-800 text-white disabled:opacity-50">
          {busy ? "Sending…" : "Submit response"}
        </button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid="sw-actions-eta">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Confirm ETA</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] uppercase text-slate-500">Expected dispatch
            <input type="date" value={expDispatch} onChange={(e) => setExpDispatch(e.target.value)}
                data-testid="sw-eta-dispatch"
                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1" />
          </label>
          <label className="text-[10px] uppercase text-slate-500">Expected delivery
            <input type="date" value={expDeliver} onChange={(e) => setExpDeliver(e.target.value)}
                data-testid="sw-eta-deliver"
                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1" />
          </label>
        </div>
        <input value={avail} onChange={(e) => setAvail(e.target.value)}
            placeholder="Available quantity" type="number"
            className="mt-2 w-full border border-slate-200 rounded-md px-2 py-1" />
        <button onClick={submitEta} disabled={busy}
            data-testid="sw-eta-submit"
            className="mt-2 w-full text-[12px] py-2 rounded-md bg-sky-800 text-white disabled:opacity-50">
          {busy ? "Saving…" : "Confirm ETA"}
        </button>
      </div>
    </div>
  );
}


function DispatchTab({ poId, d, onDone }) {
  const [lines, setLines] = useState(
    (d.outstanding_qty || []).map((o) => ({ line_id: o.line_id, product_name: o.product_name,
                                                                                    quantity: o.outstanding })));
  const [courier, setCourier] = useState("");
  const [awb, setAwb] = useState("");
  const [pkgs, setPkgs] = useState(1);
  const [dispatchDate, setDispatchDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const setLine = (i, patch) => setLines((ls) =>
    ls.map((x, ix) => ix === i ? { ...x, ...patch } : x));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/supplier/pos/${poId}/dispatch`,
        { courier, awb, package_count: parseInt(pkgs) || null,
          dispatch_date: dispatchDate || null, notes,
          lines: lines.filter(l => l.quantity > 0) });
      toast.success("Dispatch recorded");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Dispatch failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="sw-dispatch">
      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Add dispatch</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier"
              data-testid="sw-dispatch-courier"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="AWB / tracking"
              data-testid="sw-dispatch-awb"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="number" value={pkgs} onChange={(e) => setPkgs(e.target.value)}
              placeholder="Packages"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)}
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
        </div>
        <div className="mt-2 border border-slate-100 rounded">
          <table className="min-w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
              <tr><th className="p-2 text-left">Line</th><th className="p-2 text-right">Ship qty</th></tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => {
                const outstanding = (d.outstanding_qty || []).find(o => o.line_id === ln.line_id)?.outstanding ?? 0;
                const disabled = outstanding <= 0;
                return (
                  <tr key={ln.line_id} className="border-t border-slate-100">
                    <td className="p-2">{ln.product_name} <span className="text-slate-400">· max {outstanding}</span></td>
                    <td className="p-2 text-right">
                      <input type="number" min={0} max={outstanding} step="0.01"
                          value={ln.quantity}
                          disabled={disabled}
                          onChange={(e) => setLine(i, { quantity: Math.min(outstanding,
                                                        Math.max(0, parseFloat(e.target.value) || 0)) })}
                          data-testid={`sw-dispatch-qty-${ln.line_id}`}
                          className="w-24 border border-slate-200 rounded-md px-2 py-0.5 text-right disabled:bg-slate-100" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes" rows={2}
            className="mt-2 w-full text-[11px] border border-slate-200 rounded-md px-2 py-1" />
        <button onClick={submit} disabled={busy}
            data-testid="sw-dispatch-submit"
            className="mt-2 text-[12px] px-3 py-1.5 rounded-md bg-sky-800 text-white disabled:opacity-50">
          {busy ? "Saving…" : "Record dispatch"}
        </button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 p-2">Dispatch history</div>
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Courier</th>
                <th className="p-2 text-left">AWB</th><th className="p-2 text-right">Lines</th></tr>
          </thead>
          <tbody>
            {d.dispatches.map((x) => (
              <tr key={x.id} className="border-t border-slate-100">
                <td className="p-2 font-mono">{x.dispatch_date?.slice(0, 10)}</td>
                <td className="p-2">{x.courier || "—"}</td>
                <td className="p-2 font-mono">{x.awb || "—"}</td>
                <td className="p-2 text-right">{x.lines?.length || 0}</td>
              </tr>
            ))}
            {!d.dispatches.length && (
              <tr><td colSpan={4} className="p-4 text-center text-slate-400">No dispatches yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function InvoiceTab({ poId, d, onDone }) {
  const [n, setN] = useState({ supplier_invoice_number: "", invoice_date: "",
      amount: 0, tax_amount: 0, currency: "INR", kind: "INVOICE" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      let payload = { ...n, amount: parseFloat(n.amount) || 0,
                                        tax_amount: parseFloat(n.tax_amount) || 0 };
      if (file) {
        const b64 = await new Promise((r) => {
          const rd = new FileReader();
          rd.onload = () => r(rd.result.split(",")[1]);
          rd.readAsDataURL(file);
        });
        payload.file_b64 = b64; payload.file_name = file.name; payload.file_mime = file.type;
      }
      await api.post(`/supplier/pos/${poId}/invoice`, payload);
      toast.success("Invoice submitted");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="sw-invoice">
      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Upload supplier invoice</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select value={n.kind} onChange={(e) => setN({ ...n, kind: e.target.value })}
              data-testid="sw-invoice-kind"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]">
            {["INVOICE", "CREDIT_NOTE", "SUPPORTING"].map((k) =>
              <option key={k} value={k}>{k}</option>)}
          </select>
          <input value={n.supplier_invoice_number}
              onChange={(e) => setN({ ...n, supplier_invoice_number: e.target.value })}
              placeholder="Supplier Invoice #"
              data-testid="sw-invoice-number"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="date" value={n.invoice_date}
              onChange={(e) => setN({ ...n, invoice_date: e.target.value })}
              data-testid="sw-invoice-date"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input value={n.currency} onChange={(e) => setN({ ...n, currency: e.target.value })}
              placeholder="Currency"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="number" value={n.amount}
              onChange={(e) => setN({ ...n, amount: e.target.value })}
              placeholder="Invoice amount"
              data-testid="sw-invoice-amount"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="number" value={n.tax_amount}
              onChange={(e) => setN({ ...n, tax_amount: e.target.value })}
              placeholder="Tax amount"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="sw-invoice-file"
              className="col-span-2 text-[11px]" />
        </div>
        <button onClick={submit} disabled={busy || !n.supplier_invoice_number || !n.invoice_date}
            data-testid="sw-invoice-submit"
            className="mt-2 text-[12px] px-3 py-1.5 rounded-md bg-indigo-800 text-white disabled:opacity-50">
          {busy ? "Uploading…" : "Submit"}
        </button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 p-2">Submitted invoices</div>
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Kind</th>
              <th className="p-2 text-left">Number</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-left">Review</th>
            </tr>
          </thead>
          <tbody>
            {d.invoices.map((x) => (
              <tr key={x.id} className="border-t border-slate-100">
                <td className="p-2">{x.kind}</td>
                <td className="p-2 font-mono">{x.supplier_invoice_number}</td>
                <td className="p-2 font-mono">{x.invoice_date?.slice(0, 10)}</td>
                <td className="p-2 text-right font-mono">{fmtCcy(x.amount, x.currency)}</td>
                <td className="p-2">{x.review_state || "PENDING"}</td>
              </tr>
            ))}
            {!d.invoices.length && (
              <tr><td colSpan={5} className="p-4 text-center text-slate-400">No invoices submitted.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function DocumentsTab({ poId, d, onDone }) {
  const [kind, setKind] = useState("PACKING_LIST");
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) { toast.error("Pick a file"); return; }
    setBusy(true);
    try {
      const b64 = await new Promise((r) => {
        const rd = new FileReader();
        rd.onload = () => r(rd.result.split(",")[1]);
        rd.readAsDataURL(file);
      });
      await api.post(`/supplier/pos/${poId}/document`,
        { kind, file_name: file.name, file_mime: file.type, file_b64: b64, notes });
      toast.success("Document uploaded");
      setFile(null); setNotes("");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="sw-documents">
      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Upload document</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)}
              data-testid="sw-doc-kind"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]">
            {["PACKING_LIST", "WARRANTY", "SERIAL_LIST", "COMPLIANCE",
              "DELIVERY_NOTE", "DATASHEET", "OTHER"].map((k) =>
                <option key={k} value={k}>{k}</option>)}
          </select>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="sw-doc-file"
              className="col-span-2 text-[11px]" />
        </div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes" data-testid="sw-doc-notes"
            className="mt-2 w-full text-[11px] border border-slate-200 rounded-md px-2 py-1" />
        <button onClick={submit} disabled={busy || !file}
            data-testid="sw-doc-submit"
            className="mt-2 text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
          {busy ? "Uploading…" : "Upload"}
        </button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 p-2">Documents</div>
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr><th className="p-2 text-left">Kind</th><th className="p-2 text-left">File</th>
                <th className="p-2 text-left">Uploaded</th></tr>
          </thead>
          <tbody>
            {d.documents.map((x) => (
              <tr key={x.id} className="border-t border-slate-100">
                <td className="p-2">{x.kind}</td>
                <td className="p-2 font-mono text-[10px]">{x.file_name}</td>
                <td className="p-2 font-mono text-[10px]">{x.created_at?.slice(0, 19)}</td>
              </tr>
            ))}
            {!d.documents.length && (
              <tr><td colSpan={3} className="p-4 text-center text-slate-400">No documents.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AuditTab({ d }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="sw-audit">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Event</th>
              <th className="p-2 text-left">Actor</th><th className="p-2 text-left">Detail</th></tr>
        </thead>
        <tbody>
          {d.audit.map((x) => (
            <tr key={x.id} className="border-t border-slate-100">
              <td className="p-2 font-mono text-[10px]">{x.at?.slice(0, 19)}</td>
              <td className="p-2 font-semibold">{x.kind}</td>
              <td className="p-2">{x.actor}</td>
              <td className="p-2 text-slate-600">{x.detail}</td>
            </tr>
          ))}
          {!d.audit.length && (
            <tr><td colSpan={4} className="p-4 text-center text-slate-400">No events.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function MiniStat({ k, v }) {
  return (
    <div className="border border-slate-200 rounded p-2 bg-white">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-[12px] font-semibold truncate">{v ?? "—"}</div>
    </div>
  );
}


/* ================================================================
 * INTERNAL side — Supplier Response panel for Vendor PO 360
 * ================================================================ */
export function SupplierResponsePanel({ poId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!poId) return;
    api.get(`/supplier/internal/po/${poId}`)
        .then(({ data }) => setD(data))
        .catch(() => setD(null));
  }, [poId]);
  if (!d) return null;
  const latestResp = d.responses?.[0];
  const latestEta = d.etas?.[0];
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2"
        data-testid="internal-supplier-response">
      <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
        <Package className="h-3.5 w-3.5 text-indigo-700" /> Supplier collaboration
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <MiniStat k="Latest response" v={latestResp?.kind || "—"} />
        <MiniStat k="Confirmed dispatch" v={latestEta?.expected_dispatch_date?.slice(0, 10) || "—"} />
        <MiniStat k="Dispatches" v={d.dispatches.length} />
        <MiniStat k="Invoices" v={d.invoices.length} />
      </div>
      {latestResp?.reason && (
        <div className="border-l-2 border-amber-400 bg-amber-50 pl-2 py-1 text-[11px]">
          <b>Reason:</b> {latestResp.reason}
        </div>
      )}
      {d.outstanding_qty?.some((o) => o.outstanding > 0) && (
        <div className="text-[11px] text-amber-800">
          Outstanding: {d.outstanding_qty.filter((o) => o.outstanding > 0).length} line(s)
        </div>
      )}
    </div>
  );
}
